import { field, getDecodedEvent, readContract, toBool, writeContractAndWait } from "../viem";
import config from "../../config/config";
import { parseTrainedSkillEvent, parseTrainSkillRequestedEvent } from "../game";

import MafiaKillSkillAbi from "../../abis/MafiaKillSkill.json";

const address = config.killSkillAddress;
const abi = MafiaKillSkillAbi;

export interface TrainRequest {
  isPending: boolean;
  trainType: number;
  requestBlock: number;
}

export interface RequestTrainSkillResult {
  transactionHash: string;
  blockNumber: number;
  trainer: string;
  trainType: number;
  requestBlock: number;
  trainCost: number;
  timestamp: number;
}

export interface TrainSkillResult {
  transactionHash: string;
  blockNumber: number;
  trainer: string;
  trainType: number;
  isSuccess: boolean;
  trainCost: number;
  xpPoint: number;
  nextTrainTime: number;
  timestamp: number;
  hasEvent: boolean;
}

const emptyTrainRequest = (): TrainRequest => ({
  isPending: false,
  trainType: 0,
  requestBlock: 0,
});

export const requestTrainSkill = async (
  account: string,
  trainType: number
): Promise<RequestTrainSkillResult> => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "requestTrainSkill",
    args: [trainType],
    account,
    gasMultiplier: 5,
  });

  let parsed = {
    trainer: account,
    trainType,
    requestBlock: Number(tx.blockNumber),
    trainCost: 0,
    timestamp: 0,
  };

  const event = getDecodedEvent(tx, abi, "TrainSkillRequested");
  if (event) {
    parsed = parseTrainSkillRequestedEvent(event);
  }

  return {
    transactionHash: tx.transactionHash,
    blockNumber: Number(tx.blockNumber),
    ...parsed,
  };
};

export const finishTrainSkill = async (
  account: string
): Promise<TrainSkillResult> => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "finishTrainSkill",
    account,
    gasMultiplier: 5,
  });

  let parsed = {
    trainer: account,
    trainType: 0,
    isSuccess: false,
    trainCost: 0,
    xpPoint: 0,
    nextTrainTime: 0,
    timestamp: 0,
  };

  const event = getDecodedEvent(tx, abi, "TrainedSkill");
  if (event) {
    parsed = parseTrainedSkillEvent(event);
  }

  return {
    transactionHash: tx.transactionHash,
    blockNumber: Number(tx.blockNumber),
    hasEvent: Boolean(event),
    ...parsed,
  };
};

export const getTrainRequest = async (account: string): Promise<TrainRequest> => {
  const request = await readContract({
    address,
    abi,
    functionName: "trainRequests",
    args: [account],
    account,
    useWallet: true,
  });

  if (request == null) {
    return emptyTrainRequest();
  }

  return {
    isPending: toBool(field(request, "isPending", 0)),
    trainType: Number(field(request, "trainType", 1) ?? 0),
    requestBlock: Number(field(request, "requestBlock", 2) ?? 0),
  };
};

export const getTrainNonceStatus = async (account: string) => {
  const isReady = await readContract({
    address,
    abi,
    functionName: "getTrainNonceStatus",
    args: [account],
    account,
    useWallet: true,
  });

  return toBool(isReady);
};

export const getNextTrainTime = async (account: string) => {
  const nextTrainTime = await readContract<string>({
    address,
    abi,
    functionName: "nextTrainTime",
    args: [account],
    account,
  });
  return Number(nextTrainTime);
};
